import {
  Control,
  ControllerProps,
  FieldValues,
  Path,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useEffect } from "react";
import { AutocompleteProps } from "@mui/material/Autocomplete";
import { TextFieldProps } from "@mui/material/TextField";

import { useCategories } from "../hooks/useCategories";
import { Category } from "../models/Category";
import FormAutocomplete, {
  Option,
} from "../../../components/reusable-components/form/FormAutocomplete";

interface Props<T extends FieldValues = FieldValues> {
  name: Path<T>;
  control: Control<T>;
  domainId?: Option;
  disabled?: boolean;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  controllerProps?: Partial<Omit<ControllerProps<T, Path<T>>, "render">>;
  autocompleteProps?: Partial<AutocompleteProps<Option, false, false, false>>;
  textFieldProps?: TextFieldProps;
}

const CategorySelectForm = <T extends FieldValues = FieldValues>({
  name,
  control,
  domainId,
  disabled = false,
  watch,
  setValue,
  controllerProps,
  autocompleteProps,
  textFieldProps,
}: Props<T>) => {
  const { fetch } = useCategories({
    params: {
      page: 1,
      domain_id: domainId?.value,
    },
  });

  const { data: categories, loading, refetch } = fetch;

  useEffect(() => {
    if (
      (!categories.find((category) => category.id === watch(name)?.value) &&
        categories.length > 0) ||
      !domainId?.value
    ) {
      setValue(name, undefined as any);
    }
  }, [domainId?.value]);
  
  const options =
    (categories ?? []).map((category: Category) => ({
      label: category.name,
      value: category.id.toString(),
    })) || [];

  return (
    <FormAutocomplete<T, Path<T>>
      controllerProps={{
        name,
        control,
        ...controllerProps,
      }}
      autocompleteProps={{
        options,
        loading,
        disabled,
        onInputChange: (_, value) => {
          refetch({
            params: {
              search: value,
              page: 1,
              domain_id: domainId?.value,
            },
          });
        },
        ...autocompleteProps,
      }}
      textFieldProps={{
        label: "Category",
        placeholder: "Search category...",
        disabled,
        ...textFieldProps,
      }}
    />
  );
};

export default CategorySelectForm;
